import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import {
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'
import B2BCatalogApi from 'api/b2b/CatalogApiB2b'
import OrbitSession from 'hooks/useCookies'

interface CatalogListProps {
  setCatalogItems: React.Dispatch<React.SetStateAction<string[]>>
  catalogItems: string[]
}
interface CatalogRow {
  id: string
  destination: string
  catalog: string
  price: string
  dataInGB: number
  validityInDays: number
  countries: string[]
  packageType: string
  vendor: string
  pageId: string
}
function CatalogList({ setCatalogItems, catalogItems }: CatalogListProps) {
  const [searchParams] = useSearchParams()
  const [globalFilter, setGlobalFilter] = useState('')
  const [rowSelection] = useState({})
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [searchTerm, setSearchTerm] = useState('')
  const mode = searchParams.get('mode')
  // Store all accumulated data
  const [allData, setAllData] = useState([])
  const [loadedPages, setLoadedPages] = useState<Set<number>>(() => new Set())

  const [totalCount, setTotalCount] = useState(0)

  const token = OrbitSession.get('faberToken')

  // Debounce search term and reset everything when searching
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(globalFilter)
      setPage(0)
      setAllData([])
      setLoadedPages(new Set([]))
    }, 500)

    return () => clearTimeout(timer)
  }, [globalFilter])

  const shouldLoadCurrentPage = !loadedPages.has(page)

  const ids = useMemo(
    () =>
      mode === 'edit' && catalogItems.length > 0 ? catalogItems : undefined,
    [mode, catalogItems]
  )

  const initialIdsRef = useRef<string[] | undefined>(undefined)
  useEffect(() => {
    if (initialIdsRef.current === undefined && ids?.length) {
      initialIdsRef.current = ids
    }
  }, [ids])

  const isEditReady =
    mode !== 'edit' || (Array.isArray(catalogItems) && catalogItems.length > 0)

  // only send ids on the first page
  const includeIds = page === 0 && !!initialIdsRef.current

  const queryKey = [
    'catalogData',
    page,
    rowsPerPage,
    searchTerm,
    includeIds ? 'withPins' : 'noPins'
  ]

  const {
    data: catalogResponse,
    isLoading: isCatalogDataLoading,
    error,
    isFetching
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const offset = page * rowsPerPage
      const limit = rowsPerPage

      return B2BCatalogApi.getCatalogs(token, {
        offset,
        limit,
        search: searchTerm || undefined,
        ids: includeIds ? initialIdsRef.current : undefined
      })
    },
    enabled: shouldLoadCurrentPage && isEditReady,
    keepPreviousData: true, // smoother pagination
    staleTime: 300000
  })

  useEffect(() => {
    if (catalogResponse?.status === 'SUCCESS' && catalogResponse?.body?.data) {
      const newItems = catalogResponse.body.data.map((item) => ({
        id: item.catalogId,

        destination: item.catalogDisplayName,
        catalog: item.catalogName,
        price: item.prices.find(
          (p: { currency: string }) => p.currency === 'USD'
        )
          ? `${
              item.prices.find(
                (p: { currency: string }) => p.currency === 'USD'
              )!.symbol
            }${
              item.prices.find(
                (p: { currency: string }) => p.currency === 'USD'
              )!.value
            }`
          : 'N/A',
        destinationKey: item.catalogDestinationKey,
        pageId: item.catalogPageId
      }))

      setTotalCount(catalogResponse.body.total || 0)

      setAllData((prevData) => {
        const startIndex = page * rowsPerPage
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newData: any = [...prevData]

        newItems.forEach((item, index) => {
          newData[startIndex + index] = item
        })

        return newData
      })

      setLoadedPages((prev) => {
        const next = new Set(prev)
        next.add(page)
        return next
      })
    }
  }, [catalogResponse, page, rowsPerPage])

  const currentPageData = useMemo(() => {
    const startIndex = page * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return allData.slice(startIndex, endIndex)
  }, [allData, page, rowsPerPage])

  const toggleCatalogItem = (destinationKey: string) => {
    setCatalogItems((prev) => {
      if (prev.includes(destinationKey)) {
        return prev.filter((id) => id !== destinationKey)
      } else {
        return [...prev, destinationKey]
      }
    })
  }

  const columns = useMemo(
    () => [
      {
        id: 'select',
        cell: ({ row }) => {
          const rowId = row.original.destinationKey
          return (
            <Checkbox
              checked={catalogItems.includes(rowId)}
              onChange={(e) => {
                row.getToggleSelectedHandler()(e)
                toggleCatalogItem(row.original.destinationKey)
              }}
            />
          )
        },
        size: 60
      },
      {
        accessorKey: 'destination',
        header: 'Destination',
        cell: (info) => info.getValue()
      },
      {
        accessorKey: 'catalog',
        header: 'Catalog Name',
        cell: (info) => info.getValue()
      },
      {
        accessorKey: 'price',
        header: 'List Price (Read Only)',
        cell: (info) => (
          <Typography variant='body2' color='text.secondary'>
            {info.getValue()}
          </Typography>
        )
      }
    ],
    [rowSelection, page, rowsPerPage, catalogItems]
  )

  const table = useReactTable<CatalogRow>({
    data: currentPageData,
    columns,
    state: {
      pagination: {
        pageIndex: page,
        pageSize: rowsPerPage
      }
    },
    enableRowSelection: true,
    getRowId: (row) => `${row.pageId}-${row.id}`,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    pageCount: totalCount > 0 ? Math.ceil(totalCount / rowsPerPage) : -1,
    debugTable: true
  })

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    setAllData([])
    setLoadedPages(new Set([]))
  }

  if (error) {
    console.error(error)
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='error'>Error loading catalogs</Typography>
      </Box>
    )
  }
  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {/* Search Bar */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant='outlined'
          placeholder='Search catalogs...'
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: isFetching && (
              <InputAdornment position='end'>
                <CircularProgress size={20} />
              </InputAdornment>
            )
          }}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      {/* Table Container */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Table stickyHeader>
          {/* Table header */}
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      width: header.getSize(),
                      backgroundColor: 'grey.50',
                      fontWeight: 'bold'
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {isCatalogDataLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '200px'
                    }}
                  >
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>
                      Loading page {page + 1}...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : currentPageData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant='body2' color='text.secondary'>
                      {totalCount === 0 ? 'No catalogs found' : 'Loading...'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={`row-${row.id}`}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={`cell-${row.original.id}-${cell.column.id}`}
                      sx={{
                        width: cell.column.getSize()
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          component='div'
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            ...(totalCount === 0 && {
              pointerEvents: 'none',
              opacity: 0.5
            })
          }}
        />
      </Paper>

      {/* Selection Info */}
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant='body2' color='text.secondary'>
          {catalogItems.length} items are selected
        </Typography>
      </Box>
    </Box>
  )
}

export default CatalogList
